import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TRANSIT_MESSAGE_KEYS, EXT_MANAGER_KEY, MESSAGE_TYPES } from '../../cfg/endpoints';
import {
  setLatestSpreadsheetIdRequested,
  setPendingAuthCheck,
  setPendingTaskDataRequest,
  setPendingColumnOptionsRequest,
  setPendingCallLogUpdate,
} from '../../lib/redux/sheets/slice';
import { useSendWindowMessages } from './useSendWindowMessages';

// Parent (GoogleSheetsAPI) to Child (React component) message types
export const PARENT_MESSAGE_TYPES = {
  INTEGRATOR_TO_IFRAME_REQUEST_SPREADSHEET_ID: 'INTEGRATOR_TO_IFRAME_REQUEST_SPREADSHEET_ID',
  INTEGRATOR_TO_IFRAME_LOADED: 'INTEGRATOR_TO_IFRAME_LOADED',
  INTEGRATOR_TO_IFRAME_CHECK_AUTH: 'INTEGRATOR_TO_IFRAME_CHECK_AUTH',
  INTEGRATOR_TO_IFRAME_GET_TASK_DATA: 'INTEGRATOR_TO_IFRAME_GET_TASK_DATA',
  INTEGRATOR_TO_IFRAME_GET_PAGINATED_TASK_DATA: 'INTEGRATOR_TO_IFRAME_GET_PAGINATED_TASK_DATA',
  INTEGRATOR_TO_IFRAME_GET_COLUMN_OPTIONS: 'INTEGRATOR_TO_IFRAME_GET_COLUMN_OPTIONS',
  INTEGRATOR_TO_IFRAME_APPLY_CALL_LOG_UPDATE: 'INTEGRATOR_TO_IFRAME_APPLY_CALL_LOG_UPDATE',
};

// Child (React component) to Parent (GoogleSheetsAPI) message types
export const CHILD_MESSAGE_TYPES = {
  FRAME_TO_INTEGRATOR_IS_LOADED: 'FRAME_TO_INTEGRATOR_IS_LOADED',
  FRAME_TO_INTEGRATOR_COLUMN_OPTIONS: 'FRAME_TO_INTEGRATOR_COLUMN_OPTIONS',
  FRAME_TO_INTEGRATOR_AUTH_STATUS: 'FRAME_TO_INTEGRATOR_AUTH_STATUS',
  FRAME_TO_INTEGRATOR_TASK_DATA: 'FRAME_TO_INTEGRATOR_TASK_DATA',
  FRAME_TO_INTEGRATOR_PAGINATED_TASK_DATA: 'FRAME_TO_INTEGRATOR_PAGINATED_TASK_DATA',
};

const MESSAGE_KEYS = {
  TYPE: 'type',
  SPREADSHEET_ID: 'spreadsheet_id',
  SHEET_GID: 'sheet_gid',
  VALUE: 'value',
};

export enum RequestType {
  AUTH_CHECK = 'AUTH_CHECK',
  AUTHORIZE = 'AUTHORIZE',
  TASK_DATA = 'TASK_DATA',
  COLUMN_OPTIONS = 'COLUMN_OPTIONS',
  CALL_LOG_UPDATE = 'CALL_LOG_UPDATE',
}

export function useGetWindowMessages(): void {
  const dispatch = useDispatch();
  const { sendMessageToParent } = useSendWindowMessages();

  const handleMessage = (event: MessageEvent) => {
    const data = event.data;
    const type = data[MESSAGE_KEYS.TYPE]
    const spreadsheetId = data[MESSAGE_KEYS.SPREADSHEET_ID] 
    const sheetGid = data[MESSAGE_KEYS.SHEET_GID]
    const value = data[MESSAGE_KEYS.VALUE]

    if (!spreadsheetId) return;

    switch (type) {
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_REQUEST_SPREADSHEET_ID:
        dispatch(setLatestSpreadsheetIdRequested(spreadsheetId));
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_LOADED:
        sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_IS_LOADED);
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_CHECK_AUTH:
        dispatch(setLatestSpreadsheetIdRequested(spreadsheetId))
        dispatch(setPendingAuthCheck({ spreadsheetId, sheetGid }))
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_GET_TASK_DATA:
        dispatch(setPendingTaskDataRequest({ spreadsheetId, sheetGid }));
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_GET_PAGINATED_TASK_DATA:
        dispatch(setPendingTaskDataRequest({ spreadsheetId, sheetGid, pagination: value }));
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_GET_COLUMN_OPTIONS:
        if (spreadsheetId) {
          dispatch(setPendingColumnOptionsRequest({ spreadsheetId, sheetGid }));
        }
        break;
      case PARENT_MESSAGE_TYPES.INTEGRATOR_TO_IFRAME_APPLY_CALL_LOG_UPDATE:
        dispatch(setPendingCallLogUpdate({ spreadsheetId, sheetGid, data: value }));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Notify parent when component is loaded
  useEffect(() => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_IS_LOADED);
  }, []);
} 